/**
 * Phase 20F — Herbruikbaar NAW-blok (gestructureerd postaladres) met
 * automatische NL-postcode lookup via PDOK.
 *
 * Generic API:
 *   <NAWAddressBlock value={obj} onChange={(patch) => ...} disabled={false}
 *                    testidPrefix="nq" />
 *
 * `value` velden: street, house_number, house_number_addition,
 *                 postal_code, city, country
 * `onChange(patch)`: ontvangt een dict met alleen gewijzigde keys
 *
 * Auto-fill regel:
 *   - Alleen bij Nederland (of leeg country)
 *   - Alleen wanneer street EN city beide leeg zijn (geen overwrite van
 *     handmatige invoer)
 *   - 600ms debounce in usePostcodeLookup hook
 */
import React, { useEffect, useRef } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { usePostcodeLookup, postcodeLookupLabel } from "../../../lib/postcodeLookup";

function _clean(s) {
  if (s === null || s === undefined) return "";
  return String(s).trim();
}

function StatusBadge({ status, result }) {
  if (status === "idle") return null;
  const info = postcodeLookupLabel(status, result);
  const Icon = status === "loading" ? Loader2 : status === "found" ? CheckCircle2 : AlertCircle;
  const colorClass = info.color === "green"
    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
    : info.color === "amber"
    ? "bg-amber-50 border-amber-200 text-amber-800"
    : "bg-gray-50 border-gray-200 text-gray-700";
  return (
    <div
      className={`mt-1.5 flex items-center gap-1.5 text-[11px] px-2 py-1 border ${colorClass}`}
      data-testid="naw-lookup-status"
      data-status={status}
      role="status"
      aria-live="polite"
    >
      <Icon className={`w-3 h-3 flex-shrink-0 ${status === "loading" ? "animate-spin" : ""}`} />
      <span className="truncate">{info.text}</span>
    </div>
  );
}

export default function NAWAddressBlock({
  value,
  onChange,
  disabled = false,
  testidPrefix = "naw",
  title = "Adresgegevens (aanbevolen voor factuur)",
  hint = "Vul postcode + huisnummer in voor automatisch ophalen van straat en plaats (NL).",
  showWrapper = true,
  variant = "admin",
}) {
  const v = value || {};
  const set = (k, val) => onChange({ [k]: val });

  // Auto-fill state guard: onthoud welke postcode+huisnummer combinatie we
  // al hebben proberen te vullen, zodat we niet in loop raken na manual edit.
  const lastFilledRef = useRef("");

  const { status, result } = usePostcodeLookup({
    postal_code: v.postal_code,
    house_number: v.house_number,
    country: v.country,
    enabled: !disabled,
    variant,
  });

  // Vul street + city uit lookup wanneer beide leeg zijn (geen overwrite).
  useEffect(() => {
    if (status !== "found" || !result) return;
    const key = `${v.postal_code}|${v.house_number}`;
    if (lastFilledRef.current === key) return;
    const patch = {};
    if (!_clean(v.street) && result.street) patch.street = result.street;
    if (!_clean(v.city) && result.city) patch.city = result.city;
    if (!_clean(v.country) && result.country) patch.country = result.country;
    if (Object.keys(patch).length) {
      lastFilledRef.current = key;
      onChange(patch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, result]);

  const inner = (
    <>
      {title ? (
        <Label className="text-[10px] uppercase font-bold text-gray-600">{title}</Label>
      ) : null}
      {hint ? <p className="text-[10px] text-gray-500 mt-0.5 mb-1.5">{hint}</p> : null}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase">Postcode</Label>
          <Input
            data-testid={`${testidPrefix}-postal-code`}
            value={v.postal_code ?? ""}
            onChange={(e) => set("postal_code", e.target.value)}
            placeholder="3194 AG"
            disabled={disabled}
            className="rounded-none mt-1"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase">Huisnr.</Label>
          <Input
            data-testid={`${testidPrefix}-house-number`}
            value={v.house_number ?? ""}
            onChange={(e) => set("house_number", e.target.value)}
            disabled={disabled}
            className="rounded-none mt-1"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase">Toev.</Label>
          <Input
            data-testid={`${testidPrefix}-house-number-addition`}
            value={v.house_number_addition ?? ""}
            onChange={(e) => set("house_number_addition", e.target.value)}
            placeholder="A"
            disabled={disabled}
            className="rounded-none mt-1"
          />
        </div>
        <div className="md:col-span-3">
          <Label className="text-[10px] uppercase">Straatnaam</Label>
          <Input
            data-testid={`${testidPrefix}-street`}
            value={v.street ?? ""}
            onChange={(e) => set("street", e.target.value)}
            disabled={disabled}
            className="rounded-none mt-1"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase">Stad</Label>
          <Input
            data-testid={`${testidPrefix}-city`}
            value={v.city ?? ""}
            onChange={(e) => set("city", e.target.value)}
            disabled={disabled}
            className="rounded-none mt-1"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase">Land</Label>
          <Input
            data-testid={`${testidPrefix}-country`}
            value={v.country ?? ""}
            onChange={(e) => set("country", e.target.value)}
            placeholder="Nederland"
            disabled={disabled}
            className="rounded-none mt-1"
          />
        </div>
      </div>
      <StatusBadge status={status} result={result} />
    </>
  );

  if (!showWrapper) return inner;
  return (
    <div className="bg-gray-50 border border-gray-200 p-2.5" data-testid={`${testidPrefix}-address-block`}>
      {inner}
    </div>
  );
}
